import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FadeIn } from "@/components/FadeIn";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DialoguePage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", reason: "", preferred_time: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please provide your consent."); return; }
    if (!form.name || !form.email || !form.city || !form.reason) { toast.error("Please fill in all required fields."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/dialogue-request`, form);
      setSubmitted(true);
      toast.success("Your request has been submitted.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div data-testid="dialogue-page">
      {/* Hero */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <FadeIn>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl tracking-tight font-light text-foreground">
              Dialogue with Bikram
            </h1>
            <p className="font-accent italic text-lg md:text-xl text-muted-foreground mt-6">
              A one-to-one space for inquiry.
            </p>
          </div>
        </FadeIn>
      </section>

      {/* Content */}
      <section className="pb-16 md:pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mx-auto space-y-16">
          {/* What Dialogue Means */}
          <FadeIn>
            <div className="font-body text-base md:text-lg leading-relaxed text-muted-foreground space-y-5">
              <p>
                If you have questions, if you feel stuck, if you are simply curious, if you are exploring life more deeply, if you are looking for a non-judgmental space to speak openly, or if you wish to understand the work of JDF more closely, you are welcome to book a one-to-one session.
              </p>
              <p>
                Dialogue is perhaps the most direct way to dissolve illusion and see clearly. When two people meet in attention, without authority and without conclusion, illusions begin to dissolve on their own. Sometimes clarity does not come from answers, but from observing the right question fully.
              </p>
            </div>
          </FadeIn>

          {/* Quote */}
          <FadeIn>
            <blockquote className="font-accent italic text-lg md:text-xl text-foreground pl-6 border-l-2 border-[#5C6B5D]">
              "I do not have answers. But sometimes, in the very act of questioning together, something shifts. A question itself has the capacity to transform."
              <span className="block font-body not-italic text-sm text-muted-foreground mt-3">Bikram</span>
            </blockquote>
          </FadeIn>

          {/* What It Is Not */}
          <FadeIn>
            <div>
              <h3 className="font-heading text-xl md:text-2xl tracking-tight mb-4 text-foreground">What This Is Not</h3>
              <p className="font-body text-base text-muted-foreground leading-relaxed">
                This is not therapy, coaching, or spiritual guidance. It is not a consultation or an advice session. It is a shared space of observation and inquiry, without hierarchy, without a pre-set agenda. These sessions are offered freely, without any charges, as a gift to all.
              </p>
            </div>
          </FadeIn>

          <Separator style={{ backgroundColor: "#E5E4DE" }} />

          {/* Form */}
          <FadeIn>
            <div>
              <h3 className="font-heading text-2xl md:text-3xl tracking-tight mb-2 text-foreground">Request a Dialogue</h3>
              <p className="font-body text-sm text-muted-foreground mb-8">
                This is approval-based. Once submitted, the date and time will be communicated to you.
              </p>

              {submitted ? (
                <div className="p-8 border border-[#5C6B5D]/30 text-center" data-testid="dialogue-success">
                  <h4 className="font-heading text-xl text-foreground mb-2">Thank you.</h4>
                  <p className="font-body text-sm text-muted-foreground">
                    Your request has been received. We will reach out to you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="dialogue-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="font-body text-sm text-foreground">Name *</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        className="mt-2 bg-transparent border-0 border-b border-[hsl(48,12%,88%)] rounded-none px-0 py-3 font-body focus-visible:ring-0 focus-visible:border-[#5C6B5D]"
                        placeholder="Your name"
                        data-testid="dialogue-name"
                      />
                    </div>
                    <div>
                      <Label className="font-body text-sm text-foreground">Email *</Label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        className="mt-2 bg-transparent border-0 border-b border-[hsl(48,12%,88%)] rounded-none px-0 py-3 font-body focus-visible:ring-0 focus-visible:border-[#5C6B5D]"
                        placeholder="Your email"
                        data-testid="dialogue-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="font-body text-sm text-foreground">Phone (optional)</Label>
                      <Input
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        className="mt-2 bg-transparent border-0 border-b border-[hsl(48,12%,88%)] rounded-none px-0 py-3 font-body focus-visible:ring-0 focus-visible:border-[#5C6B5D]"
                        placeholder="Phone number"
                        data-testid="dialogue-phone"
                      />
                    </div>
                    <div>
                      <Label className="font-body text-sm text-foreground">City *</Label>
                      <Input
                        value={form.city}
                        onChange={(e) => update("city", e.target.value)}
                        className="mt-2 bg-transparent border-0 border-b border-[hsl(48,12%,88%)] rounded-none px-0 py-3 font-body focus-visible:ring-0 focus-visible:border-[#5C6B5D]"
                        placeholder="Your city"
                        data-testid="dialogue-city"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-body text-sm text-foreground">Why do you want to request a dialogue? *</Label>
                    <Textarea
                      value={form.reason}
                      onChange={(e) => update("reason", e.target.value)}
                      className="mt-2 bg-transparent border-0 border-b border-[hsl(48,12%,88%)] rounded-none px-0 py-3 font-body focus-visible:ring-0 focus-visible:border-[#5C6B5D] min-h-[100px] resize-none"
                      placeholder="Share openly..."
                      data-testid="dialogue-reason"
                    />
                  </div>
                  <div>
                    <Label className="font-body text-sm text-foreground">Preferred Time</Label>
                    <Input
                      value={form.preferred_time}
                      onChange={(e) => update("preferred_time", e.target.value)}
                      className="mt-2 bg-transparent border-0 border-b border-[hsl(48,12%,88%)] rounded-none px-0 py-3 font-body focus-visible:ring-0 focus-visible:border-[#5C6B5D]"
                      placeholder="e.g., Weekday mornings, Saturday afternoon"
                      data-testid="dialogue-preferred-time"
                    />
                  </div>
                  <div className="flex items-start gap-3 pt-2">
                    <Checkbox
                      checked={form.consent}
                      onCheckedChange={(checked) => update("consent", checked)}
                      className="mt-0.5 border-[hsl(48,12%,88%)] data-[state=checked]:bg-[#5C6B5D] data-[state=checked]:border-[#5C6B5D]"
                      data-testid="dialogue-consent"
                    />
                    <Label className="font-body text-sm text-muted-foreground leading-relaxed">
                      I understand that this is an approval-based request and that the date and time will be communicated after review.
                    </Label>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="font-body text-sm tracking-[0.15em] uppercase px-8 py-3 bg-[#5C6B5D] text-white hover:bg-[#5C6B5D]/90 transition-all duration-300 disabled:opacity-50"
                    data-testid="dialogue-submit"
                  >
                    {submitting ? "Submitting..." : "Submit Request"}
                  </button>
                </form>
              )}
            </div>
          </FadeIn>

          <Separator style={{ backgroundColor: "#E5E4DE" }} />

          {/* Small Group Dialogues */}
          <FadeIn>
            <div>
              <h3 className="font-heading text-2xl md:text-3xl tracking-tight mb-4 text-foreground">Small Group Dialogues</h3>
              <div className="font-body text-base text-muted-foreground leading-relaxed space-y-4">
                <p>
                  Occasionally, small dialogues are organized by friends of JDF in intimate spaces, usually with 5 to 15 participants. These gatherings are invitation-based and are not published on social media. They remain simple and quiet.
                </p>
                <p>
                  If you would like to host a dialogue in your space or city, you may reach out through the <a href="/contact" className="text-[#5C6B5D] underline underline-offset-4 decoration-1">contact page</a>. While we cannot commit to a timeline, we will reach out if a gathering is planned in your area.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
